import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useCelebrations } from './useCelebrations';
import { agruparAtualizacaoAvisos } from '@/lib/agruparAtualizacaoAvisos';

interface Aviso {
  id: string;
  titulo: string;
  mensagem: string;
  tipo: string;
  prioridade: string;
  requer_confirmacao: boolean;
  botao_confirmacao_texto: string;
  anexo_url?: string;
  anexo_nome?: string;
  criado_por_nome?: string;
  data_inicio: string;
  data_fim?: string;
  destino_tipo?: string;
  destino_role?: string;
  destino_usuario_ids?: string[];
  destino_equipe_ids?: string[];
  destino_roles?: string[];
  exibir_sempre?: boolean;
  modo_destaque?: boolean;
  metadados?: {
    pedido_id?: string;
    pedido_numero?: string | number;
    item_id?: string;
    quadro_origem?: string;
    quadro_destino?: string;
    coluna_origem?: string;
    coluna_destino?: string;
    acao_navegacao?: string;
    permite_recuperacao?: boolean;
    telefone_contato?: string;
    url_conversa_bee?: string;
    cnpj?: string;
    razao_social?: string;
    plano_nome?: string;
    bko_nome?: string;
    // transferência da IA
    origem?: string;
    conversa_id?: string;
    responsavel_id?: string;
    categoria?: string;
    cta_path_confirm?: string;
  };
}

// Função para verificar se o usuário é destinatário do aviso
export const verificarDestinatario = (aviso: Aviso, userId: string, userRole: string, userEquipeId?: string) => {
  switch (aviso.destino_tipo) {
    case 'geral':
    case 'todos':
      return true;
    
    case 'role':
      // Verificar roles múltiplas (novo) ou role única (legado)
      if (aviso.destino_roles && aviso.destino_roles.length > 0) {
        return aviso.destino_roles.includes(userRole);
      }
      if (!aviso.destino_role) return false;
      return userRole === aviso.destino_role;
    
    case 'equipe':
      // Verificar equipes múltiplas (novo) ou equipe única (legado)
      if (aviso.destino_equipe_ids && aviso.destino_equipe_ids.length > 0) {
        return userEquipeId ? aviso.destino_equipe_ids.includes(userEquipeId) : false;
      }
      return false;
    
    case 'equipe_e_role':
      // Verificar se pertence a uma das equipes E tem uma das roles
      const matchEquipe = aviso.destino_equipe_ids && aviso.destino_equipe_ids.length > 0
        ? (userEquipeId ? aviso.destino_equipe_ids.includes(userEquipeId) : false)
        : true;
      const matchRole = aviso.destino_roles && aviso.destino_roles.length > 0
        ? aviso.destino_roles.includes(userRole)
        : true;
      return matchEquipe && matchRole;
    
    case 'usuarios_especificos':
      if (!aviso.destino_usuario_ids) return false;
      return aviso.destino_usuario_ids.includes(userId);
    
    default:
      return false;
  }
};

/* Aviso confirmado numa aba some de todas (Douglas 15/10/2026): o realtime só
   ouve a tabela avisos, não a confirmação, e as outras abas seguiam com o
   Pedido novo da Conecta aberto. */
const CANAL_AVISOS = 'crm-avisos-confirmados';

function avisarOutrasAbas(avisoId: string) {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const canal = new BroadcastChannel(CANAL_AVISOS);
    canal.postMessage(avisoId);
    canal.close();
  } catch { /* navegador sem canal entre abas */ }
}

export function ouvirAvisoConfirmadoEmOutraAba(aoConfirmar: (avisoId: string) => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  try {
    const canal = new BroadcastChannel(CANAL_AVISOS);
    canal.onmessage = (e) => { if (typeof e.data === 'string') aoConfirmar(e.data); };
    return () => canal.close();
  } catch {
    return () => {};
  }
}

export function useAvisosUsuario() {
  const { user } = useAuth();
  const { notification } = useCelebrations();
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [loading, setLoading] = useState(true);
  
  const isFirstLoad = useRef(true);
  const previousAvisoIds = useRef<string[]>([]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let ativo = true;
    const atualizacao = agruparAtualizacaoAvisos(() => buscarAvisosAtivos(() => ativo));
    atualizacao.iniciar();
    const cleanup = setupRealtimeSubscription(atualizacao.solicitar);
    const pararDeOuvir = ouvirAvisoConfirmadoEmOutraAba((avisoId) => {
      setAvisos(prev => prev.filter(a => a.id !== avisoId));
    });
    return () => {
      ativo = false;
      pararDeOuvir();
      atualizacao.encerrar();
      cleanup();
    };
  }, [user]);

  const buscarAvisosAtivos = async (estaAtivo: () => boolean) => {
    if (!user) return;

    try {
      const { data: avisosAtivos, error } = await supabase
        .from('v_avisos_ativos')
        .select('*')
        // Destaques mais recentes primeiro; depois prioridade alta
        .order('data_inicio', { ascending: false })
        .order('prioridade', { ascending: false });

      if (error) throw error;

      // Buscar leituras do usuário
      const { data: leituras } = await supabase
        .from('avisos_leituras')
        .select('aviso_id, confirmado, visualizado_em')
        .eq('usuario_id', user.id);

      // Buscar role e equipe do usuário
      const { data: userData } = await supabase
        .from('usuarios')
        .select('role, equipe_id')
        .eq('id', user.id)
        .single();

      const userRole = userData?.role || '';
      const userEquipeId = (userData as any)?.equipe_id || undefined;

      // Filtrar avisos
      const avisosPendentes = (avisosAtivos || []).filter(aviso => {
        const isDestinatario = verificarDestinatario(aviso as Aviso, user.id, userRole, userEquipeId);
        if (!isDestinatario) return false;

        const leitura = leituras?.find(l => l.aviso_id === aviso.id);

        // Modo destaque: aparece SEMPRE enquanto o aviso estiver ativo (já garantido
        // pela view v_avisos_ativos: status='ativo' e dentro de data_inicio/data_fim).
        // Independe de leituras/confirmações — só some quando o admin desativar (status)
        // ou quando data_fim ultrapassar.
        if ((aviso as any).modo_destaque) {
          return true;
        }

        // Se exibir_sempre = true, mostrar sempre (exceto se confirmou hoje)
        if ((aviso as any).exibir_sempre) {
          if (!leitura) return true;
          if (aviso.requer_confirmacao && !leitura.confirmado) return true;
          // Se já visualizou hoje, não mostrar de novo na mesma sessão
          if (leitura.visualizado_em) {
            const hoje = new Date().toDateString();
            const dataVisualizacao = new Date(leitura.visualizado_em).toDateString();
            if (hoje === dataVisualizacao) return false;
          }
          return true;
        }
        
        if (aviso.requer_confirmacao) {
          return !leitura?.confirmado;
        } else {
          return !leitura;
        }
      }) as Aviso[];

      if (!estaAtivo()) return;
      setAvisos(avisosPendentes);
      
      if (!isFirstLoad.current) {
        const novosIds = avisosPendentes.map(a => a.id);
        const idsAnteriores = previousAvisoIds.current;
        const avisosNovos = avisosPendentes.filter(aviso => !idsAnteriores.includes(aviso.id));
        if (avisosNovos.some(aviso => aviso.metadados?.origem !== 'clique_email')) {
          notification();
        }
        previousAvisoIds.current = novosIds;
      } else {
        isFirstLoad.current = false;
        previousAvisoIds.current = avisosPendentes.map(a => a.id);
      }
    } catch (err) {
      console.error('❌ [AVISOS] Erro ao buscar avisos:', err);
    } finally {
      if (estaAtivo()) setLoading(false);
    }
  };

  const setupRealtimeSubscription = (atualizar: () => void) => {
    const channel = supabase
      .channel('avisos-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'avisos' },
        atualizar
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  };

  const registrarVisualizacao = async (avisoId: string) => {
    if (!user) return;
    try {
      await supabase.rpc('fn_registrar_visualizacao_aviso', {
        p_aviso_id: avisoId,
        p_usuario_id: user.id
      });
      setAvisos(prev => {
        const aviso = prev.find(a => a.id === avisoId);
        if (aviso && !aviso.requer_confirmacao && !aviso.exibir_sempre && !aviso.modo_destaque) {
          return prev.filter(a => a.id !== avisoId);
        }
        return prev;
      });
    } catch (err) {
      console.error('Erro ao registrar visualização:', err);
    }
  };

  const confirmarAviso = async (avisoId: string) => {
    if (!user) return;
    try {
      await supabase.rpc('fn_confirmar_aviso', {
        p_aviso_id: avisoId,
        p_usuario_id: user.id
      });
      setAvisos(prev => prev.filter(a => a.id !== avisoId));
      avisarOutrasAbas(avisoId);
    } catch (err) {
      console.error('Erro ao confirmar aviso:', err);
      throw err;
    }
  };

  return {
    avisos,
    loading,
    registrarVisualizacao,
    confirmarAviso
  };
}
